import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { editorialArticles } from '../data/mock';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';

const EditorialPage = () => {
  const { id } = useParams();
  const { t } = useLanguage();

  // Helper function to get translated article content
  const getTranslatedArticle = (article) => {
    const translatedContent = t(`editorial.articles.${article.id}`);
    if (translatedContent && typeof translatedContent === 'object') {
      return {
        ...article,
        title: translatedContent.title || article.title,
        excerpt: translatedContent.excerpt || article.excerpt,
        category: translatedContent.category || article.category,
        content: translatedContent.content || null
      };
    }
    return article;
  };

  // Single article view
  if (id) {
    const rawArticle = editorialArticles.find((a) => a.id === parseInt(id));

    if (!rawArticle) {
      return (
        <div className="bg-[#0B0F0E] min-h-screen">
          <Header />
          <main className="pt-32 pb-20">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] text-center">
              <h1 className="text-4xl font-semibold text-[#E6FFF6] mb-4">{t('editorial.articleNotFound')}</h1>
              <Link to="/editorial" className="text-[#52ea88] hover:underline">
                {t('editorial.backToEditorial')}
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      );
    }

    const article = getTranslatedArticle(rawArticle);

    return (
      <div className="bg-[#0B0F0E] min-h-screen">
        <Header />

        {/* Hero */}
        <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F0E] via-[#0B0F0E]/70 to-[#0B0F0E]/30" />
          </div>

          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] h-full flex items-end pb-12">
            <div className="space-y-4">
              <Link
                to="/editorial"
                className="inline-flex items-center gap-2 text-[#52ea88] hover:text-[#E6FFF6] transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('editorial.backToEditorial')}
              </Link>
              <span className="block px-3 py-1 bg-[#52ea88] text-[#0B0F0E] text-xs font-semibold uppercase tracking-wider w-fit">
                {article.category}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#E6FFF6] leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-[#9FBFB4] text-sm">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  8 {t('editorial.minRead')}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <main className="py-16">
          <div className="max-w-[800px] mx-auto px-6">
            <article className="prose prose-invert prose-lg">
              <p className="text-[#9FBFB4] text-xl leading-relaxed mb-8">
                {article.excerpt}
              </p>

              <p className="text-[#9FBFB4] text-lg leading-relaxed mb-6">
                {article.content?.intro || 'Cinema has always been a medium that challenges our perception of reality. From the earliest days of the Lumière brothers\' train arriving at a station — causing audiences to reportedly flee in terror — to the mind-bending narratives of modern science fiction, film has constantly pushed the boundaries of what we accept as "real."'}
              </p>

              <p className="text-[#9FBFB4] text-lg leading-relaxed mb-6">
                {article.content?.para1 || 'The films that question reality most effectively don\'t simply present alternative worlds; they force us to examine our assumptions about the world we inhabit. They create a space where the audience becomes an active participant in questioning their own existence.'}
              </p>

              <h2 className="text-2xl font-semibold text-[#E6FFF6] mt-12 mb-6">
                {article.content?.heading1 || 'The Philosophy of Perception'}
              </h2>

              <p className="text-[#9FBFB4] text-lg leading-relaxed mb-6">
                {article.content?.para2 || 'What separates truly great reality-questioning cinema from mere gimmickry is philosophical depth. A twist ending that reveals "it was all a dream" is cheap; a narrative that makes us question whether our own experiences might be equally illusory is profound.'}
              </p>

              <p className="text-[#9FBFB4] text-lg leading-relaxed mb-6">
                {article.content?.para3 || 'These films tap into ancient philosophical questions — Plato\'s Cave, Descartes\' Evil Demon, the Buddhist concept of Maya — and translate them into visceral, emotionally engaging experiences. They make philosophy accessible without dumbing it down.'}
              </p>

              <h2 className="text-2xl font-semibold text-[#E6FFF6] mt-12 mb-6">
                {article.content?.heading2 || 'Technical Innovation'}
              </h2>

              <p className="text-[#9FBFB4] text-lg leading-relaxed mb-6">
                {article.content?.para4 || 'The best films in this tradition don\'t just tell us that reality is uncertain; they show us through innovative cinematography, editing, and sound design. The camera becomes unreliable. Time becomes non-linear. Sound and image disconnect in unsettling ways.'}
              </p>

              <p className="text-[#9FBFB4] text-lg leading-relaxed">
                {article.content?.para5 || 'In an age where deep fakes and virtual reality are becoming increasingly sophisticated, these films feel more relevant than ever. They prepare us to question, to doubt, to examine critically — skills we desperately need in our information-saturated world.'}
              </p>
            </article>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Editorial listing
  return (
    <div className="bg-[#0B0F0E] min-h-screen">
      <Header />

      <main className="pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          {/* Page Header */}
          <div className="space-y-4 mb-12">
            <h1 className="text-4xl md:text-5xl font-semibold text-[#E6FFF6]">
              {t('sections.editorial')}
            </h1>
            <p className="text-[#9FBFB4] text-lg max-w-2xl">
              {t('editorial.description')}
            </p>
            <div className="w-20 h-1 bg-[#52ea88]"></div>
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {editorialArticles.map((rawArticle) => {
              const article = getTranslatedArticle(rawArticle);
              return (
              <Link
                key={article.id}
                to={`/editorial/${article.id}`}
                className="group block relative overflow-hidden"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F0E] via-[#0B0F0E]/50 to-transparent" />
                </div>

                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <span className="inline-block self-start px-3 py-1 bg-[#52ea88] text-[#0B0F0E] text-xs font-semibold uppercase tracking-wider mb-3">
                    {article.category}
                  </span>
                  <h2 className="text-xl font-semibold text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors mb-2">
                    {article.title}
                  </h2>
                  <p className="text-[#9FBFB4] text-sm line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-2 mt-4 text-[#52ea88] text-sm group-hover:gap-3 transition-all">
                    <span>{t('editorial.readArticle')}</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>

                <div className="absolute inset-0 border border-transparent group-hover:border-[#52ea88]/30 transition-colors duration-400" />
              </Link>
            );})}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EditorialPage;
